import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import { AttributionCode } from './tradespend_api'
import { ATTRIBUTION_CODES_COLUMNS } from './attributionCodesColumnInfo'
import { OverflownText } from '../../components/OverflownText'
import { AttributionCodeCell } from '../../components/FbUI/StagePill'
import { BiCopy } from 'react-icons/bi'
import { MdCheck } from 'react-icons/md'
import { toast } from 'react-toastify'
import { FbLink } from '../../components/FbUI/FbLink/styles'
import { formatUsdDecimal, formatInteger } from '../../utils/formatting'

// Helper function similar to createTableColumnHelper but simplified for this use case
const createAttributionCodeColumnHelper = (
  tooltips: Record<string, unknown> = {}
) => {
  return {
    retrieveColumn: (
      columnId: string,
      overrides: { meta?: Record<string, unknown> } = {}
    ) => {
      // Access the column info directly from the Record
      const columnInfo =
        ATTRIBUTION_CODES_COLUMNS[
          columnId as keyof typeof ATTRIBUTION_CODES_COLUMNS
        ]
      if (!columnInfo) {
        throw new Error(
          `Column ${columnId} not found in ATTRIBUTION_CODES_COLUMNS`
        )
      }

      return {
        id: columnId,
        header: columnInfo.header,
        meta: {
          headerGroup: columnInfo.group,
          headerTitle: columnInfo.header,
          tooltip: tooltips[columnId],
          ...(overrides.meta || {}),
        },
        ...overrides,
      }
    },
  }
}

export const useAttributionCodesTableColumns = () => {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<AttributionCode>()
  const attributionCodeColumnHelper =
    createAttributionCodeColumnHelper(tooltips)

  const cols = useMemo(
    () => [
      // Code Information Group
      columnHelper.accessor('name', {
        ...attributionCodeColumnHelper.retrieveColumn('name', {
          meta: {
            isEditable: false,
            isFirstColumn: true,
          },
        }),
        size: 300,
        cell: (info) => {
          return <AttributionCodeCell attributionCodes={[info.row.original]} />
        },
      }),
      columnHelper.accessor('description', {
        ...attributionCodeColumnHelper.retrieveColumn('description'),
        size: 250,
        cell: (info) => (
          <OverflownText maxLines={2} className="text-gray-700">
            {info.getValue() || '-'}
          </OverflownText>
        ),
      }),
      columnHelper.accessor('code', {
        ...attributionCodeColumnHelper.retrieveColumn('code'),
        size: 200,
        cell: (info) => {
          const code = info.getValue()
          const isDefault = info.row.original.is_default

          // If this is a default code, don't display any code
          if (isDefault) {
            return <span className="text-gray-400 italic">Default Code</span>
          }

          return (
            <div className="flex items-center">
              <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm mr-2">
                {code}
              </code>
              <div className="flex items-center">
                <CopyButton text={code} />
              </div>
            </div>
          )
        },
      }),
      // Custom accessor for URL
      {
        id: 'url',
        header: () => 'Shareable URL',
        meta: {
          headerGroup: 'Code Information',
          headerTitle: 'Shareable URL',
        },
        accessorFn: (row: AttributionCode) => {
          const baseUrl = `${import.meta.env.VITE_TRADESPEND_APP_HOST || 'http://localhost:5174'}/offer/${row.offer_code}`
          return row.is_default ? baseUrl : `${baseUrl}?ac=${row.code}`
        },
        size: 350,
        cell: (info: any) => {
          const url = info.getValue()
          if (!url)
            return <span className="text-gray-400 italic">Not applicable</span>

          return (
            <div className="flex items-center space-x-2">
              <FbLink to={url} target="_blank" className="truncate max-w-xs">
                {url}
              </FbLink>
              <CopyButton text={url} />
            </div>
          )
        },
      },
      // The rest of the columns remain in their original order
      columnHelper.accessor('view_count', {
        ...attributionCodeColumnHelper.retrieveColumn('view_count'),
        size: 100,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('click_count', {
        ...attributionCodeColumnHelper.retrieveColumn('click_count'),
        size: 100,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('ctr', {
        ...attributionCodeColumnHelper.retrieveColumn('ctr'),
        size: 100,
        cell: (info) => {
          const value = info.getValue() || 0
          if (value % 1 !== 0) {
            return `${value.toFixed(2)}%`
          }
          return `${value}%`
        },
      }),
      columnHelper.accessor('total_enrollments', {
        ...attributionCodeColumnHelper.retrieveColumn('total_enrollments'),
        size: 120,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('total_claims', {
        ...attributionCodeColumnHelper.retrieveColumn('total_claims'),
        size: 120,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('pending_claims', {
        ...attributionCodeColumnHelper.retrieveColumn('pending_claims'),
        size: 140,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('invalid_claims', {
        ...attributionCodeColumnHelper.retrieveColumn('invalid_claims'),
        size: 140,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('paid_claims', {
        ...attributionCodeColumnHelper.retrieveColumn('paid_claims'),
        size: 120,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('unpaid_valid_claims', {
        ...attributionCodeColumnHelper.retrieveColumn('unpaid_valid_claims'),
        size: 160,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('total_validated_units', {
        ...attributionCodeColumnHelper.retrieveColumn('total_validated_units'),
        size: 140,
        cell: (info) => formatInteger(info.getValue() || 0),
      }),
      columnHelper.accessor('total_validated_payout', {
        ...attributionCodeColumnHelper.retrieveColumn('total_validated_payout'),
        size: 150,
        cell: (info) => formatUsdDecimal(info.getValue() || 0),
      }),
      columnHelper.accessor('created_by', {
        ...attributionCodeColumnHelper.retrieveColumn('created_by'),
        size: 150,
        cell: (info) => {
          const createdBy = info.getValue()
          return createdBy
            ? `${createdBy.first_name} ${createdBy.last_name}`
            : 'System'
        },
      }),
      columnHelper.accessor('created', {
        ...attributionCodeColumnHelper.retrieveColumn('created'),
        size: 150,
        cell: (info) => {
          return format(new Date(info.getValue()), 'MMM d, yyyy')
        },
      }),
      columnHelper.accessor('modified', {
        ...attributionCodeColumnHelper.retrieveColumn('modified'),
        size: 150,
        cell: (info) => {
          return format(new Date(info.getValue()), 'MMM d, yyyy')
        },
      }),
    ],
    [columnHelper, attributionCodeColumnHelper, tooltips]
  )

  return cols
}

// Helper component for copying URLs
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        toast.success('Link copied to clipboard')
        setTimeout(() => setCopied(false), 2000)
      })
      .catch((err) => {
        console.error('Failed to copy: ', err)
        toast.error('Failed to copy link')
      })
  }

  return (
    <button
      onClick={copyToClipboard}
      className="text-gray-500 hover:text-gray-700"
      title="Copy link"
    >
      {copied ? (
        <MdCheck size={16} className="text-green-600" />
      ) : (
        <BiCopy size={16} />
      )}
    </button>
  )
}
